import moment from "moment-timezone";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import FollowUp from "common/Question/FollowUp";
import SelectOneAutoselect from "common/Question/SelectOneAutoselect";
import SelectOneRadio from "common/Question/SelectOneRadio";
import { useDefaultToast } from "lib/hooks/toasts";
import { questionProps } from "lib/propTypes";
import { useIsBreakpoint } from "lib/hooks/window";

export default function SelectOneQuestion(props, { t: _ }) {
    const {
        answer,
        id,
        isKiosk,
        autoSelect,
        question,
        readOnly,
        handleAnswer,
        handleFollowUpStart,
        toggleAnonymous,
        isAnonymous,
        handleSkip,
        response,
        submit_response: submitResponse,
        isLastQuestion,
        CustomSelectComponent,
    } = props;

    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [followUpOptional, setFollowUpOptional] = useState(false);
    const [showFollowUp, setShowFollowUp] = useState(readOnly);
    const { breakpointXS } = useIsBreakpoint();

    const toast = useDefaultToast();

    // localResponse is used to display a pre-selected response value, may be either a pre-recorded
    // answer (response.response) or a "soft" response value which isn't persisted (response)
    const [localResponse, setLocalResponse] = useState(response?.response || response);

    function getSelectedAnswer(answerId) {
        return answer.filter((a) => a.id === answerId || a.id === Number(answerId)).shift();
    }

    function handleSelect(answerId) {
        const selected = getSelectedAnswer(answerId);

        const answerData = {
            questionID: id,
            response: selected.id,
            responseText: selected.value,
        };

        if (selected.has_follow_up) {
            setSelectedAnswer({
                selected,
                answerData,
            });
            setShowFollowUp(true);
            setFollowUpOptional(selected.follow_up_optional);

            if (selected.follow_up_optional && handleFollowUpStart) {
                handleFollowUpStart(answerData);
            }

            return { submitted: false };
        }

        setShowFollowUp(false);
        setFollowUpOptional(false);

        handleAnswer(answerData);

        return { submitted: true };
    }

    function onSelect(event) {
        if (readOnly) {
            event.preventDefault();
            return;
        }

        // When an explicit selection is made ensure localResponse is only populated if there's a
        // pre-recorded answer; if the response wasn't saved in the db then this clears it
        setLocalResponse(response?.response);

        handleSelect(event.currentTarget.dataset.id);
    }

    function handleSelfIdentify(val) {
        if (toggleAnonymous != null) {
            toggleAnonymous(!val);
        }
    }

    function onFollowUp(followUp) {
        if (!followUp && !followUpOptional) {
            return;
        }

        handleAnswer({
            ...selectedAnswer.answerData,
            followUp,
            selfIdentified: !isAnonymous,
        });
        handleSelfIdentify(false);
    }

    function onDeselect(event) {
        event.preventDefault();

        setSelectedAnswer(null);
        setShowFollowUp(false);
        handleSelfIdentify(false);
    }

    useEffect(() => {
        if (typeof response === "string") {
            // If we have a response value that isn't already recorded and we're instructed to
            // submit it then trigger the selection handler here; note if there's follow-up we will
            // simply move to that screen
            const selected = getSelectedAnswer(response);
            if (selected && submitResponse) {
                const { submitted } = handleSelect(response);
                const toastMsg = submitted
                    ? _("We recorded your answer of {value}", { value: selected.value })
                    : _("You selected {value}", { value: selected.value });
                const anchorOrigin = breakpointXS ? { horizontal: "left", vertical: "top" } : null;
                toast(toastMsg, { autoHideDuration: 5000, anchorOrigin });
            }
        }
    }, []);

    const showResponseMessage = response?.response && !showFollowUp;
    const DefaultSelectComponent = autoSelect ? SelectOneAutoselect : SelectOneRadio;
    const SelectComponent =
        CustomSelectComponent != null ? CustomSelectComponent : DefaultSelectComponent;

    return (
        <div>
            {/* Hide question text during follow-up for kiosk */}
            {!showFollowUp && <label className="questions__title">{question}</label>}
            <SelectComponent
                answer={answer}
                id={id}
                handleSelect={onSelect}
                handleSkip={handleSkip}
                selectedAnswer={selectedAnswer}
                response={localResponse}
                isKiosk={isKiosk}
                showFollowUp={showFollowUp}
                isLastQuestion={isLastQuestion}
            />
            {props.follow_up && (
                <FollowUp
                    {...props}
                    selectedAnswerData={selectedAnswer?.answerData}
                    handleSelfIdentify={handleSelfIdentify}
                    handleSubmit={onFollowUp}
                    handleBack={selectedAnswer && onDeselect}
                    optional={followUpOptional}
                    visible={showFollowUp}
                    isLastQuestion={isLastQuestion}
                    selfIdentified={!isAnonymous}
                    toggleAnonymous={toggleAnonymous}
                />
            )}
            {showResponseMessage && (
                <div className="questions__question-response-notice">
                    *Your response {moment(response.time_response).fromNow()}
                </div>
            )}
        </div>
    );
}

SelectOneQuestion.contextTypes = {
    t: PropTypes.func,
};

SelectOneQuestion.propTypes = {
    ...questionProps,
    autoSelect: PropTypes.bool.isRequired,
    handleAnswer: PropTypes.func.isRequired,
    handleSkip: PropTypes.func.isRequired,
    handleFollowUpStart: PropTypes.func,
    readOnly: PropTypes.bool,
    isLastQuestion: PropTypes.bool,
    CustomSelectComponent: PropTypes.elementType,
};

SelectOneQuestion.defaultProps = {
    handleFollowUpStart: null,
    isLastQuestion: false,
    CustomSelectComponent: null,
    readOnly: false,
};
